import { factLine, type FactStatementSource } from './fact-copy.js';
import type { ReportCitation, ReportSection, ReportVersion } from './reports.js';

/**
 * How a report names what it cites, shared by the report page and the server's
 * PDF export so a source reads the same in both.
 */

/** The readings a report can cite, named as the brief names them. */
const READING_LABELS: Readonly<Record<string, string>> = {
  person_summary: 'Biography',
  club_fit: 'Club fit',
  conversation_notes: 'Conversation notes',
  relationship_read: 'Relationship',
  wealth_fit: 'Fit and interest',
  exposure_brief: 'Exposure',
  case_read: 'Case analysis',
};

export function readingLabel(kind: string): string {
  return READING_LABELS[kind] ?? 'Analysis';
}

export const DELETED_PERSON = 'Deleted person';

/** Where a cited finding quoted from the firm's own record of an attendee comes from. */
export const REPORT_FINDING_FROM_RECORD = "The firm's record";

/**
 * A cited page's site, to name it by: "state.gov" from "https://www.state.gov/…".
 * `URL` is a global in both the browser and Node; this package's own types
 * carry neither, so it is reached through `globalThis`.
 */
export function sourceHost(url: string | null): string | null {
  if (!url) return null;
  const Url = (globalThis as unknown as { URL: new (url: string) => { hostname: string } }).URL;
  try {
    return new Url(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * **Whom a passage is about**, as the page and the PDF both show it: its own
 * heading (an attendee, with their seat), else in a per-person section the
 * person its id names. The writer ids those `guest-<first 8 of the entity
 * id>-<n>` (`agent/app/reports/writing.py`, `PERSON_PASSAGE_PREFIX`). `key`
 * tells two people apart when namesakes share a name.
 */
export function reportPassagePerson(
  passage: { id: string; heading: { name: string; detail: string | null } | null },
  perPerson: boolean,
  people: readonly { entity_id: string; display_name: string | null; deleted: boolean }[],
): { key: string; name: string; detail: string | null } | null {
  if (passage.heading) {
    return { key: passage.id, name: passage.heading.name, detail: passage.heading.detail };
  }
  if (!perPerson) return null;
  const match = /^guest-([0-9a-f]{8})-/i.exec(passage.id);
  if (!match) return null;
  const key = match[1]!.toLowerCase();
  const person = people.find((p) => p.entity_id.slice(0, 8) === key);
  if (!person) return null;
  return {
    key,
    name: person.deleted || person.display_name === null ? DELETED_PERSON : person.display_name,
    detail: null,
  };
}

/**
 * **What a report's Sources list names**: everything a passage cites except the
 * office's own prose (inputs and notes), which is not a source to the reader.
 */
export type ListedCitation = Exclude<ReportCitation, { kind: 'input' | 'note' }>;

export function isListedCitation(citation: ReportCitation): citation is ListedCitation {
  return citation.kind !== 'input' && citation.kind !== 'note';
}

/** One entry per cited item, however many passages cite it. */
export function reportSourceKey(citation: ListedCitation): string {
  switch (citation.kind) {
    case 'fact':
      return `fact:${citation.lineage_id}`;
    case 'analysis':
      return `analysis:${citation.analysis_id}`;
    case 'material':
      return `material:${citation.material_id}`;
    case 'finding':
      return `finding:${citation.finding_id}`;
  }
}

/**
 * **The Sources list's numbers**, one per listed source, given in the order the
 * sections first cite them. The page numbers every section; the PDF passes only
 * the sections it exports, so a source cited only by a section left out is left
 * out with it.
 */
export function numberReportSources(sections: readonly Pick<ReportSection, 'passages'>[]): {
  numbers: Map<string, number>;
  listed: ListedCitation[];
} {
  const numbers = new Map<string, number>();
  const listed: ListedCitation[] = [];
  for (const section of sections) {
    for (const passage of section.passages) {
      for (const citation of passage.citations) {
        if (!isListedCitation(citation)) continue;
        const key = reportSourceKey(citation);
        if (numbers.has(key)) continue;
        listed.push(citation);
        numbers.set(key, listed.length);
      }
    }
  }
  return { numbers, listed };
}

/** Said of a source matched to the person on their name alone, on the page and in the PDF. */
export const NAME_MATCH_ONLY = 'name match only';

/** A listed source in parts, which the page and the PDF each set in their own way. */
export interface ReportSourceWords {
  /** Who or what the source is. */
  name: string;
  /** What the source is dated, `YYYY-MM-DD`, when it is a dated document. */
  when: string | null;
  /** Matched to the person on their name alone. */
  nameOnly: boolean;
  quote: string | null;
  url: string | null;
}

export function reportSourceWords(
  version: Pick<ReportVersion, 'cited'>,
  citation: ListedCitation,
): ReportSourceWords {
  const plain = (name: string): ReportSourceWords => ({
    name,
    when: null,
    nameOnly: false,
    quote: null,
    url: null,
  });
  switch (citation.kind) {
    case 'analysis': {
      const reading = version.cited.analyses.find((a) => a.analysis_id === citation.analysis_id);
      if (!reading) return plain('An analysis no longer on record');
      return plain(`${readingLabel(reading.kind)}, ${reading.entity_name ?? DELETED_PERSON}`);
    }
    case 'fact': {
      const fact = version.cited.facts.find((f) => f.lineage_id === citation.lineage_id);
      if (!fact) return plain('A fact no longer on record');
      const name = fact.subject_display_name ?? DELETED_PERSON;
      return plain(
        `${name}: ${factLine(fact as FactStatementSource, { id: fact.subject_entity_id, name })}`,
      );
    }
    case 'material': {
      const excerpt = version.cited.materials.find((m) => m.material_id === citation.material_id);
      if (!excerpt) return plain('A source no longer available');
      return {
        name: excerpt.issuer ?? sourceHost(excerpt.url) ?? 'A public source',
        when: excerpt.published_at ?? excerpt.retrieved_at?.slice(0, 10) ?? null,
        nameOnly: excerpt.binding === 'name_only',
        quote: excerpt.quote,
        url: excerpt.url,
      };
    }
    case 'finding': {
      const finding = version.cited.findings.find((f) => f.finding_id === citation.finding_id);
      if (!finding) return plain('A source no longer available');
      const from =
        finding.binding === 'on_record'
          ? REPORT_FINDING_FROM_RECORD
          : (sourceHost(finding.url) ?? 'A public source');
      return {
        name: `${from}, on ${finding.attendee} (${finding.reading})`,
        when: null,
        nameOnly: finding.binding === 'name_only',
        quote: finding.quote,
        url: finding.url,
      };
    }
  }
}
